// Registry of static dashboard card types.
//
// A static card is a pre-built component (Agenda, MSI Benchmarks, etc.)
// rather than a user-pinned visualisation. Add new static cards here.
//
// AI-CONTEXT: See docs/patterns.md#dashboard-static-card

import DashboardAgendaCard from './DashboardAgendaCard'
import DashboardPortfolioCard, { PORTFOLIO_CARD_TITLE } from './DashboardPortfolioCard'
import DashboardPortfolioValueCard from './DashboardPortfolioValueCard'
import DashboardHouseVisitsStatusCard from './DashboardHouseVisitsStatusCard'
import DashboardHouseVisitsHoursCard from './DashboardHouseVisitsHoursCard'
import DashboardPortfolioHouseVisitsTotalCard from './DashboardPortfolioHouseVisitsTotalCard'
import DashboardPortfolioHouseVisitsReachedCard from './DashboardPortfolioHouseVisitsReachedCard'
import DashboardPortfolioHouseVisitsStatusCard from './DashboardPortfolioHouseVisitsStatusCard'
import DashboardPortfolioHouseVisitsHoursCard from './DashboardPortfolioHouseVisitsHoursCard'
import DashboardPersonasCard from './DashboardPersonasCard'
import DashboardPersonaReportsCard from './DashboardPersonaReportsCard'
import DashboardPersonaRoutesCard from './DashboardPersonaRoutesCard'
import DashboardPersonaStatusCard from './DashboardPersonaStatusCard'
import DashboardParticipationValueCard from './DashboardParticipationValueCard'
import DashboardParticipationLadderGrowthCard from './DashboardParticipationLadderGrowthCard'
import DashboardWorkSkillsGrowthCard from './DashboardWorkSkillsGrowthCard'
import DashboardImpactPerEuroCard from './DashboardImpactPerEuroCard'
import DashboardMsiResearchDataCard from './DashboardMsiResearchDataCard'
import DashboardPlatformStatCard from './DashboardPlatformStatCard'
import DashboardMrrCard from './DashboardMrrCard'
import DashboardChurnCard from './DashboardChurnCard'
import DashboardDataQualityCard from './DashboardDataQualityCard'
import DashboardOpenProjectTasksCard from './DashboardOpenProjectTasksCard'
import DashboardUpdateRequestsCard from './DashboardUpdateRequestsCard'
import DashboardDeccaConversationsCard from './DashboardDeccaConversationsCard'
import DashboardUpcomingMomentsCard from './DashboardUpcomingMomentsCard'
import DashboardAnonymousResearchCard from './DashboardAnonymousResearchCard'
import DashboardAddressMatchesCard from './DashboardAddressMatchesCard'
import DashboardPersonaMatchesCard from './DashboardPersonaMatchesCard'
import DashboardStarredCard from './DashboardStarredCard'
import DashboardMaterialOutcomeCard from './DashboardMaterialOutcomeCard'

const DECCOS_MANAGEMENT_TENANT = '123423HJKHVKH'
const ACTIENETWERK_ENERGIEARMOEDE = '535eb957-6ec0-4cc6-b948-8529fb51de0c'

// CO2 en energierekening van materialen, opgeteld over projecten: onder het
// Actienetwerk (portfolio) of over het hele platform (Deccos). Op verzoek alleen
// voor deze twee. AI-CONTEXT: See docs/features/materiaal-uitkomsten-tegels.md
const materialOutcomeCardVisible = (currentClient) =>
    currentClient === DECCOS_MANAGEMENT_TENANT || currentClient === ACTIENETWERK_ENERGIEARMOEDE

// KPI/trend cards (the top row) share one gate: the tenant either has MSI
// effects (OutputEffects from the Meetstandaard) OR the personas module on.
const kpiCardVisible = (_client, settings) =>
    settings?.hasMSIEffects === true || settings?.personasOn === 'flex'

// Datakwaliteit beoordeelt meetdata rond MSI-effecten. Persona-only of
// fixtool-only tenants hebben geen zinvolle score om op het dashboard te tonen.
const dataQualityCardVisible = (_client, settings) => settings?.hasMSIEffects === true

// Maps item.Type → React component to render
export const STATIC_CARD_COMPONENTS = {
    'agenda': DashboardAgendaCard,
    'portfolio-projects': DashboardPortfolioCard,
    // Maatschappelijke waarde van alle projecten onder dit portfolio —
    // AI-CONTEXT: docs/features/portfolio-maatschappelijke-waarde.md
    'portfolio-value': DashboardPortfolioValueCard,
    'housevisits-status': DashboardHouseVisitsStatusCard,
    'housevisits-hours': DashboardHouseVisitsHoursCard,
    'portfolio-housevisits-total': DashboardPortfolioHouseVisitsTotalCard,
    'portfolio-housevisits-reached': DashboardPortfolioHouseVisitsReachedCard,
    'portfolio-housevisits-status': DashboardPortfolioHouseVisitsStatusCard,
    'portfolio-housevisits-hours': DashboardPortfolioHouseVisitsHoursCard,
    'personas-presence': DashboardPersonasCard,
    'personas-routes': DashboardPersonaRoutesCard,
    'personas-status': DashboardPersonaStatusCard,
    'personas-reports': DashboardPersonaReportsCard,
    'participation-ladder-growth': DashboardParticipationLadderGrowthCard,
    // Gemiddelde groei per thema van MSI Arbeidsvaardigheden —
    // AI-CONTEXT: docs/features/arbeidsvaardigheden.md
    'work-skills-growth': DashboardWorkSkillsGrowthCard,
    'participation-value': DashboardParticipationValueCard,
    'impact-per-euro': DashboardImpactPerEuroCard,
    'msi-onderzoeksdata': DashboardMsiResearchDataCard,
    // Platform-brede KPI-tegels (Deccos-beheertenant) — één component, de
    // metric volgt uit het Type. AI-CONTEXT: docs/features/supertenant-dashboard-cards.md
    'platform-projects': DashboardPlatformStatCard,
    'platform-team': DashboardPlatformStatCard,
    'platform-personas': DashboardPlatformStatCard,
    'platform-households': DashboardPlatformStatCard,
    'platform-value': DashboardPlatformStatCard,
    'platform-mrr': DashboardMrrCard,
    // Gestopte klanten — AI-CONTEXT: docs/features/platform-revenue-churn-trend.md
    'platform-churn': DashboardChurnCard,
    // Datakwaliteitsscore (bevroren nachtelijke berekening) —
    // AI-CONTEXT: docs/features/datakwaliteitsscore.md
    'data-kwaliteit': DashboardDataQualityCard,
    // Openstaande afspraken over alle projecten heen (Deccos-beheertenant) —
    // AI-CONTEXT: docs/features/deccos-projectafspraken.md
    'open-project-tasks': DashboardOpenProjectTasksCard,
    // Updateverzoeken van alle organisaties (Deccos-beheertenant) —
    // AI-CONTEXT: docs/features/update-aanvragen.md
    'update-requests': DashboardUpdateRequestsCard,
    // Laatste gesprekken van alle organisaties met Decca, Deccos-beheertenant
    // (docs/features/decca-gesprekken-overzicht.md)
    'decca-conversations': DashboardDeccaConversationsCard,
    // Wie is toe aan een volgende meting, en staat de vorige er wel —
    // AI-CONTEXT: docs/features/meetmoment-signalering.md
    'upcoming-moments': DashboardUpcomingMomentsCard,
    'starred-items': DashboardStarredCard,  // alles met een ster bij elkaar: huishoudens + deelnemers (docs/features/planner-overzicht-bkb.md)
    // Welke groepsmeting staat stil — dezelfde matrix, maar voor onderzoeken
    // zonder herleidbare invullers.
    'anonymous-research': DashboardAnonymousResearchCard,
    'address-matches': DashboardAddressMatchesCard,
    // Invullingen met de naamstap die op een koppeling wachten.
    'persona-matches': DashboardPersonaMatchesCard,
    // Bespaarde CO2 / energierekening van materialen over projecten — één
    // component, de metric volgt uit het Type.
    'materials-co2': DashboardMaterialOutcomeCard,
    'materials-energy-bill': DashboardMaterialOutcomeCard,
}

// Optional extra CSS classes per type (e.g. wide cards)
export const STATIC_CARD_CLASSES = {
    // Hele rij: zeven kolommen op de beheeromgeving passen niet in tweederde.
    'portfolio-projects': 'new-dashboard-card--full',
    'personas-presence': 'new-dashboard-card--wide',
    'participation-ladder-growth': 'new-dashboard-card--wide',
    'work-skills-growth': 'new-dashboard-card--wide',
    'participation-value': 'new-dashboard-card--kpi',
    'portfolio-value': 'new-dashboard-card--kpi',
    'impact-per-euro': 'new-dashboard-card--kpi',
    'platform-projects': 'new-dashboard-card--kpi',
    'platform-team': 'new-dashboard-card--kpi',
    'platform-personas': 'new-dashboard-card--kpi',
    'platform-households': 'new-dashboard-card--kpi',
    'platform-value': 'new-dashboard-card--kpi new-dashboard-card--kpi-double',
    'platform-mrr': 'new-dashboard-card--kpi',
    'platform-churn': 'new-dashboard-card--kpi',
    'data-kwaliteit': 'new-dashboard-card--kpi',
    'portfolio-housevisits-total': 'new-dashboard-card--kpi',
    'portfolio-housevisits-reached': 'new-dashboard-card--kpi',
    'open-project-tasks': 'new-dashboard-card--wide',
    'update-requests': 'new-dashboard-card--wide',
    'decca-conversations': 'new-dashboard-card--wide',
    'upcoming-moments': 'new-dashboard-card--wide',
    'anonymous-research': 'new-dashboard-card--wide',
    'materials-co2': 'new-dashboard-card--kpi',
    'materials-energy-bill': 'new-dashboard-card--kpi',
}

// Which drag zone a card type renders in. Cards default to the 'main' grid.
// 'kpi' cards render in a separate top row that has its own horizontal drag
// context, so they can only be reordered amongst themselves (left ↔ right).
export const STATIC_CARD_ZONES = {
    'participation-value': 'kpi',
    'portfolio-value': 'kpi',
    'impact-per-euro': 'kpi',
    'platform-projects': 'kpi',
    'platform-team': 'kpi',
    'platform-personas': 'kpi',
    'platform-households': 'kpi',
    'platform-value': 'kpi',
    'platform-mrr': 'kpi',
    'platform-churn': 'kpi',
    'data-kwaliteit': 'kpi',
    'portfolio-housevisits-total': 'kpi',
    'portfolio-housevisits-reached': 'kpi',
    'materials-co2': 'kpi',
    'materials-energy-bill': 'kpi',
}

// Returns the drag zone for a card type: 'kpi' or 'main' (the default).
export const getCardZone = (type) => (STATIC_CARD_ZONES[type] === 'kpi' ? 'kpi' : 'main')

// Restricts some card types to specific organisations.
// Values can be an array of CompagnyIDs or a function (client, settings) => boolean.
export const STATIC_CARD_VISIBILITY = {
    'portfolio-projects': (currentClient, settings) =>
        currentClient === DECCOS_MANAGEMENT_TENANT || settings?.portfolio === 'block',
    'housevisits-status': (_client, settings) => settings?.housevisits === true,
    'housevisits-hours': (_client, settings) => settings?.housevisits === true,
    // Portfolio-housevisits cards: visible only for portfolio tenants. The card body
    // also handles the "no child projects with housevisits" empty state.
    // Alleen voor een portfolio: de kaart telt de projecten eronder op.
    'portfolio-value': (_client, settings) => settings?.portfolio === 'block',
    'portfolio-housevisits-total': (_client, settings) => settings?.portfolio === 'block',
    'portfolio-housevisits-reached': (_client, settings) => settings?.portfolio === 'block',
    'portfolio-housevisits-status': (_client, settings) => settings?.portfolio === 'block',
    'portfolio-housevisits-hours': (_client, settings) => settings?.portfolio === 'block',
    'personas-presence': (_client, settings) => settings?.personasOn === 'flex',
    'personas-routes': (_client, settings) => settings?.personasOn === 'flex',
    'personas-status': (_client, settings) => settings?.personasOn === 'flex',
    'personas-reports': (_client, settings) => settings?.personasOn === 'flex',
    'participation-ladder-growth': (_client, settings) => settings?.personasOn === 'flex',
    // Zelfde poort als het tabblad zelf: deelnemers aan, en de impactmodule aan
    // (PersonaBuilder hangt het tabblad Arbeidsvaardigheden achter beide).
    'work-skills-growth': (_client, settings) =>
        settings?.personasOn === 'flex' && settings?.impactFunctionality === true,
    // KPI/trend row — tenant has MSI effects OR the personas module on.
    'participation-value': kpiCardVisible,
    'impact-per-euro': kpiCardVisible,
    'data-kwaliteit': dataQualityCardVisible,
    // Meetstandaard-onderzoekers — alleen de Deccos-beheertenant.
    'msi-onderzoeksdata': (currentClient) => currentClient === DECCOS_MANAGEMENT_TENANT,
    // Platform-brede KPI-tegels — alleen de Deccos-beheertenant.
    'platform-projects': (currentClient) => currentClient === DECCOS_MANAGEMENT_TENANT,
    'platform-team': (currentClient) => currentClient === DECCOS_MANAGEMENT_TENANT,
    'platform-personas': (currentClient) => currentClient === DECCOS_MANAGEMENT_TENANT,
    'platform-households': (currentClient) => currentClient === DECCOS_MANAGEMENT_TENANT,
    'platform-value': (currentClient) => currentClient === DECCOS_MANAGEMENT_TENANT,
    'platform-mrr': (currentClient) => currentClient === DECCOS_MANAGEMENT_TENANT,
    'platform-churn': (currentClient) => currentClient === DECCOS_MANAGEMENT_TENANT,
    // Openstaande projectafspraken — alleen de Deccos-beheertenant.
    'open-project-tasks': (currentClient) => currentClient === DECCOS_MANAGEMENT_TENANT,
    // Updateverzoeken van klanten — alleen de Deccos-beheertenant.
    'update-requests': (currentClient) => currentClient === DECCOS_MANAGEMENT_TENANT,
    // Gesprekken van alle organisaties met Decca — alleen de Deccos-beheertenant.
    // De callable erachter weigert elke andere organisatie; dit is het
    // weergavefilter, niet de toegangspoort.
    'decca-conversations': (currentClient) => currentClient === DECCOS_MANAGEMENT_TENANT,
    // Aankomende meetmomenten: zinvol zodra de organisatie met deelnemers of
    // met huishoudens werkt. Zonder een van beide is er niemand om te volgen.
    'upcoming-moments': (_client, settings) =>
        settings?.personasOn === 'flex' || settings?.housevisits === true,
    // Met ster: dezelfde poort — er moet iemand zijn om een ster te geven.
    'starred-items': (_client, settings) =>
        settings?.personasOn === 'flex' || settings?.housevisits === true,
    // Anonieme onderzoeken hangen niet aan deelnemers of huishoudens — een
    // groepsmeting heeft er per definitie geen. De poort is dus het meten zelf.
    'anonymous-research': (_client, settings) => settings?.impactFunctionality === true,
    // Twijfelgevallen van de koppeling op adres: die ontstaan bij een intake en
    // bij de adresstap, en allebei gaan ze over huishoudens.
    // AI-CONTEXT: See docs/features/adres-koppeling-algemene-qr.md
    'address-matches': (_client, settings) => settings?.housevisits === true,
    // Invullingen met de naamstap: die gaan over deelnemers.
    // AI-CONTEXT: See docs/features/deelnemer-koppeling-algemene-link.md
    'persona-matches': (_client, settings) => settings?.personasOn === 'flex',
    'materials-co2': materialOutcomeCardVisible,
    'materials-energy-bill': materialOutcomeCardVisible,
}

// Human-readable labels used in the restore list when the stored Label
// is missing. Keep in sync with STATIC_CARD_COMPONENTS keys.
export const STATIC_CARD_LABELS = {
    'upcoming-moments': 'Aankomende meetmomenten',
    'starred-items': 'Met ster',
    'anonymous-research': 'Anonieme onderzoeken',
    'address-matches': 'Adressen om te koppelen',
    'persona-matches': 'Deelnemers om te koppelen',
    'agenda': 'Agenda',
    'portfolio-projects': PORTFOLIO_CARD_TITLE,
    'portfolio-value': 'Maatschappelijke waarde projecten',
    'housevisits-status': 'Status + langst wachtende',
    'housevisits-hours': 'Cumulatieve uren gewerkt',
    'portfolio-housevisits-total': 'Totaal huishoudens (portfolio)',
    'portfolio-housevisits-reached': 'Bereikte huishoudens (portfolio)',
    'portfolio-housevisits-status': 'Status huishoudens (portfolio)',
    'portfolio-housevisits-hours': 'Cumulatieve uren (portfolio)',
    'personas-presence': 'Aanwezigheid',
    'personas-routes': 'Trajecten',
    'personas-status': 'Status',
    'personas-reports': 'Laatste rapporten',
    'participation-ladder-growth': 'Groei participatieladder',
    'work-skills-growth': 'Groei arbeidsvaardigheden',
    'participation-value': 'Maatschappelijke waarde',
    'impact-per-euro': 'Impact per euro',
    'msi-onderzoeksdata': 'Onderzoeksdata meetstandaard',
    'platform-projects': 'Actieve projecten',
    'platform-team': 'Teamleden totaal',
    'platform-personas': 'Deelnemers totaal',
    'platform-households': 'Huishoudens totaal',
    'platform-value': 'Maatschappelijke waarde totaal',
    'platform-mrr': 'Maandelijkse omzet',
    'platform-churn': 'Churn',
    'data-kwaliteit': 'Datakwaliteit',
    'open-project-tasks': 'Openstaande afspraken',
    'update-requests': 'Updateverzoeken',
    'decca-conversations': 'Gesprekken met Decca',
    'materials-co2': 'Bespaarde CO2',
    'materials-energy-bill': 'Bespaard op energierekening',
}

// Category pill per card type, matching the pill each card renders in its own
// header via StaticCardLabel. Keep in sync with the card components. Types
// without a pill on the card itself are omitted here too.
/** @type {Record<string, { variant: string, label: string }>} */
export const STATIC_CARD_PILLS = {
    'portfolio-projects': { variant: 'portfolio', label: 'Portfolio' },
    'portfolio-value': { variant: 'portfolio', label: 'Portfolio' },
    'open-project-tasks': { variant: 'portfolio', label: 'Portfolio' },
    'update-requests': { variant: 'msi', label: 'Deccos' },
    'decca-conversations': { variant: 'msi', label: 'Deccos' },
    'housevisits-status': { variant: 'housevisits', label: 'Huishoudens' },
    'housevisits-hours': { variant: 'housevisits', label: 'Huishoudens' },
    'address-matches': { variant: 'housevisits', label: 'Huishoudens' },
    'persona-matches': { variant: 'personas', label: 'Deelnemers' },
    'portfolio-housevisits-total': { variant: 'housevisits', label: 'Portfolio' },
    'portfolio-housevisits-reached': { variant: 'housevisits', label: 'Portfolio' },
    'portfolio-housevisits-status': { variant: 'housevisits', label: 'Portfolio' },
    'portfolio-housevisits-hours': { variant: 'housevisits', label: 'Portfolio' },
    'personas-presence': { variant: 'personas', label: 'Medewerkers' },
    'personas-routes': { variant: 'personas', label: 'Medewerkers' },
    'personas-status': { variant: 'personas', label: 'Medewerkers' },
    'personas-reports': { variant: 'personas', label: 'Medewerkers' },
    'work-skills-growth': { variant: 'personas', label: 'Medewerkers' },
    'platform-projects': { variant: 'msi', label: 'Deccos' },
    'platform-team': { variant: 'msi', label: 'Deccos' },
    'platform-personas': { variant: 'msi', label: 'Deccos' },
    'platform-households': { variant: 'msi', label: 'Deccos' },
    'platform-value': { variant: 'msi', label: 'Deccos' },
    'platform-mrr': { variant: 'msi', label: 'Deccos' },
    'platform-churn': { variant: 'msi', label: 'Deccos' },
}

// Returns true if a given static card type is allowed for the current client.
// Entries can be an array of CompagnyIDs or a function (client, settings) => boolean.
export const isStaticCardVisible = (type, currentClient, settings) => {
    const allowed = STATIC_CARD_VISIBILITY[type]
    if (!allowed) return true
    if (typeof allowed === 'function') return allowed(currentClient, settings)
    return allowed.includes(currentClient)
}

// Returns true if the type is registered (known to this build).
export const isKnownStaticCardType = (type) =>
    Object.prototype.hasOwnProperty.call(STATIC_CARD_COMPONENTS, type)
